import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box, 
    Grid, 
    Card, 
    CardMedia,
    CardContent,
    Typography, 
    TextField, 
    Button, 
    IconButton, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Chip, 
    Paper, 
    Badge, 
    Drawer
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CircleIcon from '@mui/icons-material/Circle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import StarIcon from '@mui/icons-material/Star';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import HistoryIcon from '@mui/icons-material/History';
import MenuIcon from '@mui/icons-material/Menu';

import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { printElement, BluetoothPrinterManager, printOrderReceipt } from '../utils/printHelper';

export default function QuickPOSTerminalView() {
    const {
        activeCart,
        addToCart,
        removeFromCart,
        menuItems,
        orders = [],
        settleCounterBill,
        showToast,
        lockedActions,
        categories,
        setActiveCart,
        setSidebarCollapsed,
        setSidebarMobileOpen,
        pinnedItems = [],
        togglePinItem,
        branchSettings,
        posCardLayout = 'compact2',
        showMenuImages = false
    } = useApp();


    const { currentLanguage } = useLanguage();

    // Local States
    const [orderSource, setOrderSource] = useState('takeaway'); // takeaway, dine_in, delivery
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [discount, setDiscount] = useState(0);
    const [discountReason, setDiscountReason] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash'); // cash, upi, split
    const [splitCashAmount, setSplitCashAmount] = useState(0);
    const [splitUpiAmount, setSplitUpiAmount] = useState(0);

    // Change Calculator
    const [receivedCash, setReceivedCash] = useState('');

    // Dialogs & Drawers
    const [openUpiQr, setOpenUpiQr] = useState(false);
    const [openReceipt, setOpenReceipt] = useState(false);
    const [settledOrderDetails, setSettledOrderDetails] = useState(null);
    const [receiptPreviewImage, setReceiptPreviewImage] = useState(null);
    const [mobileCartOpen, setMobileCartOpen] = useState(false);
    const [openSplitDialog, setOpenSplitDialog] = useState(false);

    const btDeviceName = localStorage.getItem('dinexa_printer_device_name') || '';

    // Collapse sidebar for maximum billing area
    useEffect(() => {
        if (setSidebarCollapsed) {
            setSidebarCollapsed(true);
        }
        return () => {
            if (setSidebarCollapsed) setSidebarCollapsed(false);
        };
    }, [setSidebarCollapsed]);

    // Localized Labels
    const labels = {
        mr: {
            title: 'झटपट काउंटर व कूपन बिलिंग',
            subtitle: 'स्नॅक सेंटर / फास्ट फूड फास्ट बिलिंग',
            allCategory: 'सर्व पदार्थ',
            favorites: '📌 खास / लोकप्रिय',
            searchPlaceholder: 'पदार्थ शोधा (उदा. चहा, वडापाव, समोसा)...',
            quickAdd: 'जोडा',
            cartTitle: 'चालू बिल व टोकन',
            clearCart: 'रिकामे करा',
            emptyCartText: 'कार्ट रिकामी आहे! डावीकडून पदार्थ निवडा.',
            totalPayable: 'एकूण देय रक्कम',
            subtotal: 'एकूण किंमत',
            gst: 'जीएसटी (५%)',
            discount: 'सवलत/डिस्काउंट',
            cashBtn: '💵 रोख बिल (Cash)',
            upiBtn: '📱 UPI QR पेमेंट',
            splitBtn: '⚡ स्प्लिट (Cash+UPI)',
            tokenNumber: 'टोकन क्रमांक',
            billSettled: 'बिल यशस्वीरित्या सेव्ह झाले!',
            nextOrder: 'पुढील ऑर्डर (Next)',
            changeToReturn: 'परत द्यावयाची रक्कम',
            cashReceived: 'मिळालेली रोख रक्कम',
            upiTitle: 'UPI QR स्कॅन करून पैसे भरा',
            upiHelp: 'ग्राहकाला कोणत्याही UPI ॲपवरून (PhonePe/GPay/Paytm) स्कॅन करण्यास सांगा.',
            confirmPaid: '✅ पैसे मिळाले (Confirm & Token)',
            parcel: '📦 पार्सल',
            dineIn: '🍽️ काउंटर',
            itemsCount: 'पदार्थ'
        },
        hi: {
            title: 'क्विक काउंटर व कूपन बिलिंग',
            subtitle: 'स्नैक सेंटर / फास्ट फूड फास्ट बिलिंग',
            allCategory: 'सभी पदार्थ',
            favorites: '📌 पसंदीदा',
            searchPlaceholder: 'पदार्थ खोजें (उदा. चाय, समोसा, डोसा)...',
            quickAdd: 'जोड़ें',
            cartTitle: 'चालू बिल व टोकन',
            clearCart: 'खाली करें',
            emptyCartText: 'कार्ट खाली है! पदार्थ चुनकर बिलिंग शुरू करें।',
            totalPayable: 'कुल देय राशि',
            subtotal: 'कुल राशि',
            gst: 'जीएसटी (५%)',
            discount: 'छूट/डिस्काउंट',
            cashBtn: '💵 नकद बिल (Cash)',
            upiBtn: '📱 UPI QR पेमेंट',
            splitBtn: '⚡ स्प्लिट (Cash+UPI)',
            tokenNumber: 'टोकन संख्या',
            billSettled: 'बिल सफलतापूर्वक दर्ज हुआ!',
            nextOrder: 'अगली ऑर्डर (Next)',
            changeToReturn: 'वापस देने योग्य राशि',
            cashReceived: 'प्राप्त नकद राशि',
            upiTitle: 'UPI QR स्कैन करके भुगतान करें',
            upiHelp: 'ग्राहक को किसी भी UPI ऐप (PhonePe/GPay/Paytm) से स्कैन करने कहें।',
            confirmPaid: '✅ भुगतान प्राप्त हुआ (Confirm & Token)',
            parcel: '📦 पार्सल',
            dineIn: '🍽️ काउंटर',
            itemsCount: 'पदार्थ'
        },
        en: {
            title: 'Quick Counter & Token POS',
            subtitle: 'Snack Center / Fast Food High-Speed Billing',
            allCategory: 'All Items',
            favorites: '📌 Favorites',
            searchPlaceholder: 'Search items (e.g. Tea, Vada, Sandwich)...',
            quickAdd: 'Add',
            cartTitle: 'Current Token Bill',
            clearCart: 'Clear Cart',
            emptyCartText: 'Cart is empty! Select dishes to start billing.',
            totalPayable: 'Total Payable',
            subtotal: 'Subtotal',
            gst: 'GST (5%)',
            discount: 'Discount',
            cashBtn: '💵 Cash Bill',
            upiBtn: '📱 UPI QR Pay',
            splitBtn: '⚡ Split (Cash+UPI)',
            tokenNumber: 'Token Number',
            billSettled: 'Bill Settled Successfully!',
            nextOrder: 'Next Order',
            changeToReturn: 'Change to Return',
            cashReceived: 'Cash Received',
            upiTitle: 'Scan to Pay via UPI',
            upiHelp: 'Ask customer to scan and pay via PhonePe, GPay, Paytm, or BHIM.',
            confirmPaid: '✅ Payment Received (Confirm & Token)',
            parcel: '📦 Takeaway',
            dineIn: '🍽️ Dine-In',
            itemsCount: 'Items'
        }
    };

    const l = labels[currentLanguage] || labels.mr;

    const getDisplayName = (item) => {
        if (!item) return '';
        if (currentLanguage === 'mr') return item.name_mr || item.nameMr || item.name || 'Dish';
        if (currentLanguage === 'hi') return item.name_hi || item.nameHi || item.name || 'Dish';
        return item.name || item.name_mr || item.nameMr || 'Dish';
    };

    const getDishImage = (item) => {
        let src = item?.image_url || item?.image || item?.photo || item?.photo_url || '';
        if (!src) return '/images/default_food.png';
        if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) return src;
        if (src.startsWith('/')) return src;
        return `/${src}`;
    };

    const getCategoryDisplayName = (cat) => {
        if (!cat) return '';
        if (currentLanguage === 'mr') return cat.name_mr || cat.name;
        if (currentLanguage === 'hi') return cat.name_hi || cat.name;
        return cat.name;
    };

    const checkIsVeg = (item) => {
        if (!item) return true;
        if (item.veg === true || item.veg === 1 || item.veg === '1' || item.veg === 'true') return true;
        if (item.is_veg === true || item.is_veg === 1 || item.is_veg === '1' || item.is_veg === 'true') return true;

        let catName = (item.category || item.category_name || '').toString().toLowerCase();
        if (catName.includes('non') || catName.includes('नॉन')) return false;

        const nameStr = ((item.name || '') + ' ' + (item.nameMr || '') + ' ' + (item.name_mr || '')).toLowerCase();
        if (nameStr.includes('chicken') || nameStr.includes('mutton') || nameStr.includes('egg') || nameStr.includes('fish') || nameStr.includes('चिकन') || nameStr.includes('मटण') || nameStr.includes('अंडे') || nameStr.includes('मासे')) {
            return false;
        }
        return true;
    };

    // Calculate totals
    const enableGst = branchSettings 
        ? !!branchSettings.gst_enabled 
        : (localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1');
    
    const subtotal = (activeCart || []).reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)), 0);
    const tax = enableGst ? (subtotal * 0.05) : 0;
    const finalTotal = Math.max(0, subtotal + tax - (parseFloat(discount) || 0));
    const totalItemsCount = (activeCart || []).reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);

    // Sync split payment default
    useEffect(() => {
        setSplitCashAmount(Math.round(finalTotal));
        setSplitUpiAmount(0);
    }, [finalTotal]);

    // Fast Item Filter
    const filteredMenu = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return (menuItems || [])
            .filter(item => {
                const matchesSearch = !query || 
                    (item.name || '').toLowerCase().includes(query) || 
                    (item.nameMr || item.name_mr || '').toLowerCase().includes(query) || 
                    (item.nameHi || item.name_hi || '').toLowerCase().includes(query);
                
                if (!matchesSearch) return false;

                if (selectedCategory === 'all') return true;
                if (selectedCategory === 'favorites') return Array.isArray(pinnedItems) && pinnedItems.includes(item.id);

                return item.category_id == selectedCategory || 
                    item.category == selectedCategory || 
                    (item.category_name && item.category_name.toString().toLowerCase() === selectedCategory.toString().toLowerCase());
            })
            .sort((a, b) => {
                const isPinArray = Array.isArray(pinnedItems);
                const aPinned = isPinArray && pinnedItems.includes(a.id);
                const bPinned = isPinArray && pinnedItems.includes(b.id);
                if (aPinned && !bPinned) return -1;
                if (!aPinned && bPinned) return 1;
                return (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0);
            });
    }, [menuItems, searchQuery, selectedCategory, pinnedItems]);

    // Item Cart Quantity Map for quick badge rendering
    const cartQtyMap = useMemo(() => {
        const map = {};
        (activeCart || []).forEach(item => {
            map[item.id] = item.quantity;
        });
        return map;
    }, [activeCart]);

    // Recent Settled Tokens (Top 4-5) for 1-Click Quick Reprint
    const recentTokens = useMemo(() => {
        if (!Array.isArray(orders)) return [];
        return [...orders]
            .filter(o => o && (o.status === 'served' || o.status === 'completed' || o.status === 'settled' || o.status === 'paid' || o.paymentMethod || o.payment_method || o.coupon_number || o.token_number || o.couponNumber || o.tokenNumber))
            .sort((a, b) => new Date(b.created_at || b.timestamp || b.createdAt || 0) - new Date(a.created_at || a.timestamp || a.createdAt || 0))
            .slice(0, 5);
    }, [orders]);

    const handleReprintRecentToken = async (order) => {
        if (!order) return;
        setSettledOrderDetails(order);
        setReceivedCash('');
        const receiptLang = localStorage.getItem('dinexa_printer_lang') || currentLanguage;
        try {
            const preview = await BluetoothPrinterManager.generateReceiptDataURL(order, receiptLang);
            setReceiptPreviewImage(preview);
        } catch (err) {
            console.warn("Failed to generate preview for recent order:", err);
            setReceiptPreviewImage(null);
        }
        setOpenReceipt(true);
    };

    // Handle Quick Settle
    const handleQuickSettle = (mode = 'cash') => {
        if (!activeCart || activeCart.length === 0) {
            showToast("⚠️ कृपया आधी कार्टमध्ये पदार्थ जोडा!");
            return;
        }

        setPaymentMethod(mode);

        if (mode === 'upi') {
            setOpenUpiQr(true);
        } else if (mode === 'split') {
            setOpenSplitDialog(true);
        } else {
            executeSettlement('cash');
        }
    };

    const executeSettlement = async (pMethod = 'cash') => {
        let appliedPayment = pMethod;
        if (pMethod === 'split') {
            appliedPayment = `Split (Cash: ₹${splitCashAmount}, UPI: ₹${splitUpiAmount})`;
        }

        const orderRecord = settleCounterBill(appliedPayment, parseFloat(discount) || 0, 0, discountReason, orderSource);
        
        if (orderRecord) {
            setSettledOrderDetails(orderRecord);
            setMobileCartOpen(false);
            setOpenUpiQr(false);
            setOpenSplitDialog(false);
            setDiscount(0);
            setDiscountReason('');
            setReceivedCash('');

            const connectionType = localStorage.getItem('dinexa_printer_connection_type') || 'system';
            const receiptLang = localStorage.getItem('dinexa_printer_lang') || currentLanguage;

            try {
                if (connectionType !== 'system') {
                    try {
                        await BluetoothPrinterManager.printReceiptCanvas(orderRecord, receiptLang);
                    } catch (e) {
                        const preview = await BluetoothPrinterManager.generateReceiptDataURL(orderRecord, receiptLang);
                        setReceiptPreviewImage(preview);
                        setOpenReceipt(true);
                    }
                } else {
                    const preview = await BluetoothPrinterManager.generateReceiptDataURL(orderRecord, receiptLang);
                    setReceiptPreviewImage(preview);
                    setOpenReceipt(true);
                }
            } catch (err) {
                console.warn("Print preview fallback:", err);
                setOpenReceipt(true);
            }
        }
    };

    const handleBtPrint = async () => {
        try {
            const receiptLang = localStorage.getItem('dinexa_printer_lang') || currentLanguage;
            await BluetoothPrinterManager.printReceiptCanvas(settledOrderDetails, receiptLang);
            setOpenReceipt(false);
        } catch (e) {
            console.warn("Bluetooth printing failed:", e);
            showToast("⚠️ ब्लूटूथ प्रिंटर एरर: " + (e.message || 'प्रिंटर कनेक्ट नाही'));
        }
    };

    // Calculate Change
    const parsedReceived = parseFloat(receivedCash) || 0;
    const changeToReturn = Math.max(0, parsedReceived - (settledOrderDetails?.total || finalTotal));

    // Dynamic UPI QR String
    const upiVpa = branchSettings?.upi_id || localStorage.getItem('dinexa_upi_id') || 'dinexa.pos@upi';
    const merchantName = branchSettings?.name || 'Dinexa Snack POS';
    const upiPayUrl = `upi://pay?pa=${encodeURIComponent(upiVpa)}&pn=${encodeURIComponent(merchantName)}&am=${finalTotal.toFixed(2)}&cu=INR`;
    const qrCodeApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(upiPayUrl)}`;

    return (
        <Box sx={{ 
            height: '100%', 
            maxHeight: '100%',
            display: 'flex', 
            bgcolor: '#f1f5f9',
            overflow: 'hidden',
            fontFamily: 'inherit'
        }}>
            
            {/* 1. LEFT MAIN SECTION (Top Header + Dishes Catalog + Bottom More/Tokens Bar) */}
            <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%', 
                overflow: 'hidden' 
            }}>
                
                {/* 1A. TOP HEADER & FILTER BAR */}
                <Paper elevation={0} sx={{ 
                    p: { xs: 1, md: 1.2 }, 
                    bgcolor: '#ffffff', 
                    borderBottom: '1.5px solid #cbd5e1',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    zIndex: 10,
                    flexShrink: 0
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        
                        {/* Brand & Mode */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                                bgcolor: '#008751', 
                                color: '#ffffff', 
                                p: 0.7, 
                                borderRadius: 2, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,135,81,0.3)'
                            }}>
                                <FlashOnIcon sx={{ fontSize: { xs: 20, md: 22 } }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1.1, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>
                                    {l.title}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                                    {l.subtitle}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Order Channel Selector (Takeaway / Dine-in) */}
                        <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#f1f5f9', p: 0.3, borderRadius: 2, border: '1px solid #cbd5e1' }}>
                            <Chip 
                                label={l.parcel}
                                size="small"
                                onClick={() => setOrderSource('takeaway')}
                                sx={{ 
                                    fontWeight: 800, 
                                    cursor: 'pointer',
                                    bgcolor: orderSource === 'takeaway' ? '#008751' : 'transparent',
                                    color: orderSource === 'takeaway' ? '#ffffff' : '#475569',
                                    '&:hover': { bgcolor: orderSource === 'takeaway' ? '#006f42' : '#e2e8f0' }
                                }}
                            />
                            <Chip 
                                label={l.dineIn}
                                size="small"
                                onClick={() => setOrderSource('dine_in')}
                                sx={{ 
                                    fontWeight: 800, 
                                    cursor: 'pointer',
                                    bgcolor: orderSource === 'dine_in' ? '#008751' : 'transparent',
                                    color: orderSource === 'dine_in' ? '#ffffff' : '#475569',
                                    '&:hover': { bgcolor: orderSource === 'dine_in' ? '#006f42' : '#e2e8f0' }
                                }}
                            />
                        </Box>

                        {/* Search Field */}
                        <Box sx={{ minWidth: { xs: '100%', sm: 200, md: 240 }, flex: { xs: 1, sm: 'initial' } }}>
                            <TextField 
                                size="small"
                                fullWidth
                                placeholder={l.searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 0.8, fontSize: 18 }} />,
                                    endAdornment: searchQuery ? (
                                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                                            <ClearIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    ) : null,
                                    sx: { 
                                        borderRadius: 2.5, 
                                        bgcolor: '#f8fafc', 
                                        fontSize: '0.82rem', 
                                        height: 36 
                                    }
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Horizontal Category Scroller */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 0.8, 
                        overflowX: 'auto', 
                        pb: 0.3, 
                        pt: 0.3,
                        '::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none'
                    }}>
                        <Chip 
                            label={l.allCategory}
                            clickable
                            onClick={() => setSelectedCategory('all')}
                            sx={{ 
                                fontWeight: 800, 
                                fontSize: '0.82rem',
                                py: 1.8,
                                px: 0.4,
                                bgcolor: selectedCategory === 'all' ? '#0f172a' : '#ffffff',
                                color: selectedCategory === 'all' ? '#ffffff' : '#334155',
                                border: '1px solid',
                                borderColor: selectedCategory === 'all' ? '#0f172a' : '#cbd5e1',
                                boxShadow: selectedCategory === 'all' ? '0 2px 8px rgba(15,23,42,0.2)' : 'none',
                                '&:hover': { bgcolor: selectedCategory === 'all' ? '#1e293b' : '#f1f5f9' }
                            }}
                        />

                        <Chip 
                            icon={<StarIcon sx={{ fontSize: '15px !important', color: selectedCategory === 'favorites' ? '#f59e0b !important' : '#94a3b8 !important' }} />}
                            label={l.favorites}
                            clickable
                            onClick={() => setSelectedCategory('favorites')}
                            sx={{ 
                                fontWeight: 800, 
                                fontSize: '0.82rem',
                                py: 1.8,
                                px: 0.4,
                                bgcolor: selectedCategory === 'favorites' ? '#008751' : '#ffffff',
                                color: selectedCategory === 'favorites' ? '#ffffff' : '#334155',
                                border: '1px solid',
                                borderColor: selectedCategory === 'favorites' ? '#008751' : '#cbd5e1',
                                '&:hover': { bgcolor: selectedCategory === 'favorites' ? '#006f42' : '#f1f5f9' }
                            }}
                        />

                        {(categories || []).map(cat => {
                            const isSelected = selectedCategory == cat.id || selectedCategory === cat.name;
                            return (
                                <Chip 
                                    key={cat.id}
                                    label={getCategoryDisplayName(cat)}
                                    clickable
                                    onClick={() => setSelectedCategory(cat.id)}
                                    sx={{ 
                                        fontWeight: 800, 
                                        fontSize: '0.82rem',
                                        py: 1.8,
                                        px: 0.4,
                                        bgcolor: isSelected ? '#008751' : '#ffffff',
                                        color: isSelected ? '#ffffff' : '#334155',
                                        border: '1px solid',
                                        borderColor: isSelected ? '#008751' : '#cbd5e1',
                                        boxShadow: isSelected ? '0 2px 8px rgba(0,135,81,0.25)' : 'none',
                                        '&:hover': { bgcolor: isSelected ? '#006f42' : '#f1f5f9' }
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Paper>

                {/* 1B. DISH CATALOG SCROLL AREA */}
                <Box sx={{ 
                    flex: 1, 
                    p: { xs: 1, sm: 1.2, md: 1.5 }, 
                    overflowY: 'auto', 
                    pb: { xs: 8, md: 1.5 }
                }}>
                    {filteredMenu.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
                            <FastfoodIcon sx={{ fontSize: 54, mb: 1, opacity: 0.5 }} />
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>कोणतेही पदार्थ सापडले नाहीत</Typography>
                            <Typography variant="body2">कृपया वेगळा शब्द शोधून पहा किंवा दुसरी कॅटेगरी निवडा.</Typography>
                        </Box>
                    ) : (
                        <>
                            {/* DESKTOP UNIFORM DISHES GRID (>= md) */}
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'flex-start' }}>
                                    {filteredMenu.map(item => {
                                        const isVeg = checkIsVeg(item);
                                        const currentQty = cartQtyMap[item.id] || 0;
                                        const isPinned = Array.isArray(pinnedItems) && pinnedItems.includes(item.id);

                                        return (
                                            <Box key={`desk-${item.id}`} sx={{ width: 175, height: 225, flexShrink: 0, flexGrow: 0 }}>
                                                <Card 
                                                    elevation={0}
                                                    onClick={() => addToCart(item)}
                                                    sx={{ 
                                                        width: 175,
                                                        minWidth: 175,
                                                        maxWidth: 175,
                                                        height: 225,
                                                        minHeight: 225,
                                                        maxHeight: 225,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'space-between',
                                                        borderRadius: 2.5,
                                                        cursor: 'pointer',
                                                        border: currentQty > 0 ? '2px solid #008751' : '1px solid #e2e8f0',
                                                        bgcolor: currentQty > 0 ? '#f0fdf4' : '#ffffff',
                                                        boxShadow: currentQty > 0 ? '0 4px 14px rgba(0,135,81,0.18)' : '0 2px 6px rgba(0,0,0,0.04)',
                                                        transition: 'all 0.15s ease',
                                                        overflow: 'hidden',
                                                        position: 'relative',
                                                        '&:hover': {
                                                            borderColor: '#008751',
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 6px 18px rgba(0,0,0,0.09)'
                                                        },
                                                        '&:active': { transform: 'scale(0.98)' }
                                                    }}
                                                >
                                                    {/* Dish Photo Area (Fixed 110px) */}
                                                    <Box sx={{ position: 'relative', width: '100%', height: 110, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                                                        <CardMedia
                                                            component="img"
                                                            image={getDishImage(item)}
                                                            alt={getDisplayName(item)}
                                                            onError={(e) => { e.target.onerror = null; e.target.src = '/images/default_food.png'; }}
                                                            sx={{ 
                                                                width: '100%', 
                                                                height: '100%', 
                                                                objectFit: 'cover'
                                                            }}
                                                        />

                                                        {/* Veg/Non-Veg Tag */}
                                                        <Box sx={{ 
                                                            position: 'absolute', 
                                                            top: 6, 
                                                            left: 6, 
                                                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                                                            backdropFilter: 'blur(4px)',
                                                            border: `1.5px solid ${isVeg ? '#16a34a' : '#dc2626'}`, 
                                                            borderRadius: 1, 
                                                            p: '2px 4px', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: 0.4,
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                                        }}>
                                                            <CircleIcon sx={{ fontSize: 7, color: isVeg ? '#16a34a' : '#dc2626' }} />
                                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: isVeg ? '#166534' : '#991b1b', lineHeight: 1 }}>
                                                                {isVeg ? 'VEG' : 'NON-VEG'}
                                                            </Typography>
                                                        </Box>

                                                        {/* Pin Button */}
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                if (togglePinItem) togglePinItem(item.id); 
                                                            }}
                                                            sx={{ 
                                                                position: 'absolute', 
                                                                top: 6, 
                                                                right: 6, 
                                                                bgcolor: isPinned ? '#ea580c' : 'rgba(255, 255, 255, 0.9)',
                                                                color: isPinned ? '#ffffff' : '#64748b',
                                                                p: 0.4,
                                                                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                                                '&:hover': { bgcolor: isPinned ? '#c2410c' : '#ffffff' }
                                                            }}
                                                        >
                                                            {isPinned ? <StarIcon sx={{ fontSize: 15 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 15 }} />}
                                                        </IconButton>
                                                    </Box>

                                                    {/* Dish Details */}
                                                    <CardContent sx={{ p: 1, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', '&:last-child': { pb: 1 } }}>
                                                        <Typography 
                                                            variant="subtitle2" 
                                                            sx={{ 
                                                                fontWeight: 900, 
                                                                color: '#1e293b', 
                                                                lineHeight: 1.25, 
                                                                fontSize: '0.88rem',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                height: '2.4em',
                                                                minHeight: '2.4em'
                                                            }}
                                                        >
                                                            {getDisplayName(item)}
                                                        </Typography>

                                                        {/* Bottom Row: Price & Quantity Controls */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 0.5, borderTop: '1px dashed #e2e8f0', height: 32 }}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#008751', fontSize: '1rem' }}>
                                                                ₹{Number(item.price || 0).toFixed(0)}
                                                            </Typography>

                                                            {currentQty === 0 ? (
                                                                <Button 
                                                                    variant="contained" 
                                                                    size="small"
                                                                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        addToCart(item);
                                                                    }}
                                                                    sx={{ 
                                                                        borderRadius: 2, 
                                                                        py: 0.2, 
                                                                        px: 1, 
                                                                        fontSize: '0.75rem', 
                                                                        fontWeight: 900, 
                                                                        bgcolor: '#008751', 
                                                                        color: '#ffffff',
                                                                        textTransform: 'none',
                                                                        boxShadow: 'none',
                                                                        minWidth: 55,
                                                                        '&:hover': { bgcolor: '#006f42', boxShadow: 'none' }
                                                                    }}
                                                                >
                                                                    {l.quickAdd}
                                                                </Button>
                                                            ) : (
                                                                <Box 
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    sx={{ 
                                                                        display: 'flex', 
                                                                        alignItems: 'center', 
                                                                        bgcolor: '#008751', 
                                                                        borderRadius: 2, 
                                                                        p: 0.2 
                                                                    }}
                                                                >
                                                                    <IconButton 
                                                                        size="small" 
                                                                        onClick={() => removeFromCart(item.id)}
                                                                        sx={{ color: '#ffffff', p: 0.2 }}
                                                                    >
                                                                        <RemoveIcon sx={{ fontSize: 14 }} />
                                                                    </IconButton>
                                                                    <Typography sx={{ color: '#ffffff', fontWeight: 900, px: 0.6, fontSize: '0.82rem' }}>
                                                                        {currentQty}
                                                                    </Typography>
                                                                    <IconButton 
                                                                        size="small" 
                                                                        onClick={() => addToCart(item)}
                                                                        sx={{ color: '#ffffff', p: 0.2 }}
                                                                    >
                                                                        <AddIcon sx={{ fontSize: 14 }} />
                                                                    </IconButton>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* MOBILE DISHES LIST STRIPS (< md) */}
                            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
                                {filteredMenu.map(item => {
                                    const isVeg = checkIsVeg(item);
                                    const currentQty = cartQtyMap[item.id] || 0;

                                    return (
                                        <Box
                                            key={`mob-${item.id}`}
                                            onClick={() => addToCart(item)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 1,
                                                bgcolor: currentQty > 0 ? '#f0fdf4' : '#ffffff',
                                                borderRadius: 2.5,
                                                border: currentQty > 0 ? '1.5px solid #008751' : '1px solid #e2e8f0',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                                cursor: 'pointer',
                                                gap: 1.2,
                                                transition: 'all 0.15s ease',
                                                '&:active': { transform: 'scale(0.98)' }
                                            }}
                                        >
                                            {/* Left: Thumbnail Image */}
                                            <Box sx={{ position: 'relative', width: 62, height: 62, borderRadius: 2, overflow: 'hidden', flexShrink: 0, bgcolor: '#f1f5f9' }}>
                                                <img 
                                                    src={getDishImage(item)} 
                                                    alt={getDisplayName(item)} 
                                                    onError={(e) => { e.target.onerror = null; e.target.src = '/images/default_food.png'; }}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: 3,
                                                    left: 3,
                                                    bgcolor: 'rgba(255,255,255,0.95)',
                                                    borderRadius: 0.6,
                                                    p: '1px 3px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: `1px solid ${isVeg ? '#16a34a' : '#dc2626'}`
                                                }}>
                                                    <CircleIcon sx={{ fontSize: 6, color: isVeg ? '#16a34a' : '#dc2626' }} />
                                                </Box>
                                            </Box>

                                            {/* Middle: Dish Name & Price */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 900, color: '#1e293b', fontSize: '0.92rem', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {getDisplayName(item)}
                                                </Typography>
                                                <Typography sx={{ fontWeight: 900, color: '#008751', fontSize: '1rem', mt: 0.3 }}>
                                                    ₹{Number(item.price || 0).toFixed(0)}
                                                </Typography>
                                            </Box>

                                            {/* Right: Quantity Stepper or Add Button */}
                                            <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                                                {currentQty === 0 ? (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                                        onClick={() => addToCart(item)}
                                                        sx={{
                                                            borderRadius: 2,
                                                            py: 0.5,
                                                            px: 1.2,
                                                            fontWeight: 900,
                                                            fontSize: '0.78rem',
                                                            bgcolor: '#008751',
                                                            color: '#ffffff',
                                                            textTransform: 'none',
                                                            whiteSpace: 'nowrap',
                                                            '&:hover': { bgcolor: '#006f42' }
                                                        }}
                                                    >
                                                        {l.quickAdd}
                                                    </Button>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#008751', borderRadius: 2, p: 0.2 }}>
                                                        <IconButton size="small" onClick={() => removeFromCart(item.id)} sx={{ color: '#fff', p: 0.3 }}>
                                                            <RemoveIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                        <Typography sx={{ color: '#fff', fontWeight: 900, px: 0.8, fontSize: '0.85rem' }}>
                                                            {currentQty}
                                                        </Typography>
                                                        <IconButton size="small" onClick={() => addToCart(item)} sx={{ color: '#fff', p: 0.3 }}>
                                                            <AddIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Box>

                {/* 1C. BOTTOM BAR: MORE BUTTON + RECENT TOKENS QUICK STRIP IN ONE SINGLE LINE */}
                <Paper 
                    elevation={2} 
                    sx={{ 
                        p: { xs: 0.8, sm: 1 }, 
                        px: { xs: 1, sm: 1.5 },
                        borderTop: '2px solid #cbd5e1', 
                        bgcolor: '#ffffff', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        gap: 1.2,
                        zIndex: 4,
                        flexShrink: 0
                    }}
                >
                    {/* Left Group: More Button (Desktop Only) + Tokens Strip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flex: 1, minWidth: 0, overflowX: 'auto', '::-webkit-scrollbar': { display: 'none' } }}>
                        
                        {/* More (अधिक) Button - Desktop Only */}
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<MenuIcon sx={{ fontSize: 18 }} />}
                            onClick={() => setSidebarMobileOpen(true)}
                            sx={{
                                display: { xs: 'none', md: 'inline-flex' },
                                bgcolor: '#2563eb',
                                color: '#ffffff',
                                fontWeight: 900,
                                fontSize: '0.82rem',
                                borderRadius: 2,
                                px: 1.5,
                                py: 0.6,
                                textTransform: 'none',
                                boxShadow: '0 2px 6px rgba(37,99,235,0.3)',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#1d4ed8' }
                            }}
                        >
                            {currentLanguage === 'mr' ? 'अधिक (More)' : (currentLanguage === 'hi' ? 'अधिक (More)' : 'More')}
                        </Button>

                        {/* Recent Tokens Strip in Distinct Container (Takes full width on mobile) */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.8, 
                            bgcolor: '#f1f5f9', 
                            p: '4px 10px', 
                            borderRadius: 2.5, 
                            border: '1.5px solid #cbd5e1',
                            flex: { xs: 1, md: 'initial' },
                            overflowX: 'auto',
                            '::-webkit-scrollbar': { display: 'none' }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <HistoryIcon sx={{ fontSize: 17, color: '#008751' }} />
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#1e293b', whiteSpace: 'nowrap' }}>
                                    {currentLanguage === 'mr' ? 'मागील टोकन्स:' : 'Recent:'}
                                </Typography>
                            </Box>

                            {recentTokens.length === 0 ? (
                                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                                    (अजून टोकन्स नाहीत)
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    {recentTokens.map((recentOrd, idx) => {
                                        const tokenNum = recentOrd.coupon_number || recentOrd.token_number || (recentOrd.id ? recentOrd.id.replace('ORD-', '') : idx + 1);
                                        const amount = Math.round(Number(recentOrd.total || recentOrd.final_total || 0));
                                        return (
                                            <Chip
                                                key={recentOrd.id || idx}
                                                icon={<LocalPrintshopIcon sx={{ fontSize: '13px !important', color: '#008751 !important' }} />}
                                                label={`#${tokenNum} • ₹${amount}`}
                                                clickable
                                                onClick={() => handleReprintRecentToken(recentOrd)}
                                                sx={{
                                                    fontWeight: 900,
                                                    fontSize: '0.78rem',
                                                    bgcolor: '#ffffff',
                                                    color: '#166534',
                                                    border: '1.5px solid #86efac',
                                                    height: 28,
                                                    cursor: 'pointer',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': {
                                                        bgcolor: '#dcfce7',
                                                        borderColor: '#22c55e',
                                                        transform: 'scale(1.04)',
                                                        boxShadow: '0 2px 8px rgba(0,135,81,0.2)'
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Right Hint */}
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap', display: { xs: 'none', lg: 'block' } }}>
                        💡 1-क्लिक पावती रिप्रिंट / व्ह्यू
                    </Typography>
                </Paper>
            </Box>

            {/* 2. RIGHT SECTION: FULL-HEIGHT DESKTOP CART (>= 900px) */}
            <Paper 
                elevation={4}
                sx={{ 
                    width: { md: 360, lg: 390, xl: 410 }, 
                    bgcolor: '#ffffff', 
                    borderLeft: '2.5px solid #cbd5e1', 
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    height: '100%',
                    zIndex: 5
                }}
            >
                {/* Cart Header (Starts at the very top of the page!) */}
                <Box sx={{ p: 1.5, px: 2, borderBottom: '2px solid #0f172a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#ffffff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Badge badgeContent={totalItemsCount} color="success">
                            <ShoppingCartIcon sx={{ color: '#008751', fontSize: 24 }} />
                        </Badge>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a', fontSize: '1.1rem' }}>
                            {l.cartTitle}
                        </Typography>
                    </Box>
                    {activeCart.length > 0 && (
                        <Button 
                            size="small" 
                            color="error" 
                            startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setActiveCart([])}
                            sx={{ fontWeight: 800, fontSize: '0.78rem', textTransform: 'none', py: 0.2, px: 1 }}
                        >
                            {l.clearCart}
                        </Button>
                    )}
                </Box>

                {/* Cart Items List (Scrollable - Full Vertical Space) */}
                <Box sx={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    p: 1.5, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1.2 
                }}>
                    {activeCart.length === 0 ? (
                        <Box sx={{ my: 'auto', textAlign: 'center', color: '#94a3b8', p: 2 }}>
                            <ShoppingCartIcon sx={{ fontSize: 48, mb: 1, opacity: 0.35 }} />
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#64748b' }}>{l.emptyCartText}</Typography>
                        </Box>
                    ) : (
                        activeCart.map(item => (
                            <Box 
                                key={item.id}
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    p: 1.2, 
                                    bgcolor: '#f8fafc', 
                                    borderRadius: 2.5, 
                                    border: '1px solid #e2e8f0',
                                    transition: 'all 0.15s ease',
                                    '&:hover': { bgcolor: '#f1f5f9', borderColor: '#cbd5e1' }
                                }}
                            >
                                {/* Item Name & Calculation */}
                                <Box sx={{ flex: 1, pr: 1.5 }}>
                                    <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: '0.98rem', lineHeight: 1.25, mb: 0.3 }}>
                                        {getDisplayName(item)}
                                    </Typography>
                                    <Typography sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.85rem' }}>
                                        ₹{item.price} × {item.quantity} = <strong style={{ color: '#008751', fontWeight: 900 }}>₹{(item.price * item.quantity).toFixed(0)}</strong>
                                    </Typography>
                                </Box>

                                {/* Touch Stepper */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    bgcolor: '#ffffff', 
                                    borderRadius: 2, 
                                    border: '1.5px solid #cbd5e1',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    overflow: 'hidden'
                                }}>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => removeFromCart(item.id)} 
                                        sx={{ p: 0.6, color: '#dc2626', borderRadius: 0, '&:hover': { bgcolor: '#fee2e2' } }}
                                    >
                                        <RemoveIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <Typography sx={{ fontWeight: 900, px: 1.2, fontSize: '0.95rem', minWidth: 24, textAlign: 'center', color: '#0f172a' }}>
                                        {item.quantity}
                                    </Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => addToCart(item)} 
                                        sx={{ p: 0.6, color: '#16a34a', borderRadius: 0, '&:hover': { bgcolor: '#dcfce7' } }}
                                    >
                                        <AddIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>

                {/* Cart Footer: Bill Breakdown & Settlement */}
                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                    
                    {/* Compact Discount Input */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TextField 
                            size="small"
                            placeholder={l.discount}
                            type="number"
                            value={discount || ''}
                            onChange={(e) => setDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                            disabled={lockedActions && lockedActions.includes('apply_discount')}
                            sx={{ bgcolor: '#ffffff', flex: 1 }}
                            InputProps={{
                                startAdornment: <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', mr: 0.5 }}>₹ सवलत</Typography>,
                                sx: { borderRadius: 2, fontSize: '0.82rem', height: 32 }
                            }}
                        />
                        {discount > 0 && (
                            <IconButton size="small" onClick={() => setDiscount(0)} color="error" sx={{ p: 0.5 }}>
                                <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>

                    {/* Subtotal & GST */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{l.subtotal}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155' }}>₹{subtotal.toFixed(0)}</Typography>
                    </Box>

                    {enableGst && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{l.gst}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155' }}>₹{tax.toFixed(0)}</Typography>
                        </Box>
                    )}

                    {discount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, color: '#dc2626' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{l.discount}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 900 }}>-₹{parseFloat(discount).toFixed(0)}</Typography>
                        </Box>
                    )}

                    {/* Total Payable */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 0.8, pt: 0.8, borderTop: '2px dashed #cbd5e1' }}>
                        <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: '1rem' }}>{l.totalPayable}</Typography>
                        <Typography sx={{ fontWeight: 900, color: '#008751', fontSize: '1.4rem' }}>₹{finalTotal.toFixed(0)}</Typography>
                    </Box>

                    {/* Fast Settle Buttons in ONE row */}
                    <Box sx={{ display: 'flex', gap: 0.8, mt: 1 }}>
                        <Button 
                            variant="contained"
                            fullWidth
                            disabled={activeCart.length === 0}
                            onClick={() => handleQuickSettle('cash')}
                            startIcon={<PaymentsIcon sx={{ fontSize: '18px !important' }} />}
                            sx={{ 
                                flex: 1.2,
                                py: 1.1, 
                                px: 0.5,
                                fontWeight: 900, 
                                fontSize: '0.88rem', 
                                borderRadius: 2, 
                                bgcolor: '#008751', 
                                boxShadow: '0 4px 12px rgba(0,135,81,0.3)',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#006f42' }
                            }}
                        >
                            💵 रोख ₹{finalTotal.toFixed(0)}
                        </Button>

                        <Button 
                            variant="contained"
                            fullWidth
                            disabled={activeCart.length === 0}
                            onClick={() => handleQuickSettle('upi')}
                            startIcon={<QrCodeIcon sx={{ fontSize: '18px !important' }} />}
                            sx={{ 
                                flex: 1,
                                py: 1.1, 
                                px: 0.5,
                                fontWeight: 900, 
                                fontSize: '0.88rem', 
                                borderRadius: 2, 
                                bgcolor: '#0284c7', 
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#0369a1' }
                            }}
                        >
                            📱 UPI QR
                        </Button>

                        <Button 
                            variant="outlined"
                            fullWidth
                            disabled={activeCart.length === 0}
                            onClick={() => handleQuickSettle('split')}
                            startIcon={<AccountBalanceWalletIcon sx={{ fontSize: '16px !important' }} />}
                            sx={{ 
                                flex: 0.85,
                                py: 1.1, 
                                px: 0.5,
                                fontWeight: 900, 
                                fontSize: '0.82rem', 
                                borderRadius: 2, 
                                color: '#334155', 
                                borderColor: '#cbd5e1', 
                                bgcolor: '#ffffff',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                            }}
                        >
                            ⚡ Split
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* 3. MOBILE FLOATING 2-BUTTON BOTTOM BAR (< 900px) */}
            <Paper 
                elevation={8}
                sx={{ 
                    display: { xs: 'block', md: 'none' },
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: '#ffffff',
                    borderTop: '2px solid #cbd5e1',
                    p: 1,
                    zIndex: 1000
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 48 }}>
                    {/* 1. More (अधिक) Button (Blue) */}
                    <Button
                        variant="contained"
                        onClick={() => setSidebarMobileOpen(true)}
                        startIcon={<MenuIcon sx={{ fontSize: 20 }} />}
                        sx={{
                            flex: 1,
                            height: '100%',
                            bgcolor: '#2563eb',
                            color: '#ffffff',
                            fontWeight: 900,
                            fontSize: '0.9rem',
                            borderRadius: 2.5,
                            textTransform: 'none',
                            boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                            '&:hover': { bgcolor: '#1d4ed8' }
                        }}
                    >
                        {currentLanguage === 'mr' ? 'अधिक (More)' : (currentLanguage === 'hi' ? 'अधिक (More)' : 'More')}
                    </Button>

                    {/* 2. Payment (पेमेंट / कार्ट) Button (Green) */}
                    <Button
                        variant="contained"
                        onClick={() => setMobileCartOpen(true)}
                        startIcon={
                            <Badge badgeContent={totalItemsCount} color="error">
                                <ShoppingCartIcon sx={{ fontSize: 20 }} />
                            </Badge>
                        }
                        sx={{
                            flex: 1.5,
                            height: '100%',
                            bgcolor: '#008751',
                            color: '#ffffff',
                            fontWeight: 900,
                            fontSize: '0.95rem',
                            borderRadius: 2.5,
                            textTransform: 'none',
                            boxShadow: '0 2px 10px rgba(0,135,81,0.35)',
                            '&:hover': { bgcolor: '#006f42' }
                        }}
                    >
                        {totalItemsCount > 0 ? `पेमेंट / बिल ₹${finalTotal.toFixed(0)}` : 'पेमेंट / कार्ट'}
                    </Button>
                </Box>
            </Paper>

            {/* 4. MOBILE CART DRAWER (Bottom Sheet on Mobile) */}
            <Drawer
                anchor="bottom"
                open={mobileCartOpen}
                onClose={() => setMobileCartOpen(false)}
                PaperProps={{
                    sx: { 
                        borderTopLeftRadius: 24, 
                        borderTopRightRadius: 24, 
                        maxHeight: '85vh',
                        bgcolor: '#ffffff',
                        p: 2
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5, borderBottom: '2px solid #0f172a' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingCartIcon sx={{ color: '#008751', fontSize: 22 }} />
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            {l.cartTitle} ({totalItemsCount})
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {activeCart.length > 0 && (
                            <Button 
                                size="small" 
                                color="error" 
                                startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                onClick={() => {
                                    setActiveCart([]);
                                    setMobileCartOpen(false);
                                }}
                                sx={{ fontWeight: 800, fontSize: '0.78rem', textTransform: 'none' }}
                            >
                                {l.clearCart}
                            </Button>
                        )}
                        <IconButton size="small" onClick={() => setMobileCartOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Items List */}
                <Box sx={{ my: 1.5, maxHeight: '38vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {activeCart.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                            <ShoppingCartIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#64748b' }}>{l.emptyCartText}</Typography>
                        </Box>
                    ) : (
                        activeCart.map(item => (
                            <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Box sx={{ flex: 1, pr: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 900, color: '#1e293b' }}>{getDisplayName(item)}</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>₹{item.price} × {item.quantity} = <strong style={{ color: '#008751' }}>₹{(item.price * item.quantity).toFixed(0)}</strong></Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#ffffff', borderRadius: 2, border: '1.5px solid #cbd5e1' }}>
                                    <IconButton size="small" onClick={() => removeFromCart(item.id)} sx={{ p: 0.4, color: '#dc2626' }}>
                                        <RemoveIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <Typography sx={{ fontWeight: 900, px: 1, color: '#0f172a' }}>{item.quantity}</Typography>
                                    <IconButton size="small" onClick={() => addToCart(item)} sx={{ p: 0.4, color: '#16a34a' }}>
                                        <AddIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>

                {/* Discount & Settle */}
                <Box sx={{ pt: 1.5, borderTop: '2px solid #cbd5e1' }}>
                    {/* Discount Input */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TextField 
                            size="small"
                            placeholder={l.discount}
                            type="number"
                            value={discount || ''}
                            onChange={(e) => setDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                            disabled={lockedActions && lockedActions.includes('apply_discount')}
                            sx={{ bgcolor: '#ffffff', flex: 1 }}
                            InputProps={{
                                startAdornment: <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', mr: 0.5 }}>₹ सवलत</Typography>,
                                sx: { borderRadius: 2, fontSize: '0.82rem', height: 34 }
                            }}
                        />
                        {discount > 0 && (
                            <IconButton size="small" onClick={() => setDiscount(0)} color="error" sx={{ p: 0.5 }}>
                                <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>

                    {/* Subtotal & GST */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{l.subtotal}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155' }}>₹{subtotal.toFixed(0)}</Typography>
                    </Box>

                    {enableGst && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{l.gst}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155' }}>₹{tax.toFixed(0)}</Typography>
                        </Box>
                    )}

                    {discount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, color: '#dc2626' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{l.discount}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 900 }}>-₹{parseFloat(discount).toFixed(0)}</Typography>
                        </Box>
                    )}

                    {/* Total Payable Line */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1, pt: 0.8, borderTop: '2px dashed #cbd5e1' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a' }}>{l.totalPayable}</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#008751' }}>₹{finalTotal.toFixed(0)}</Typography>
                    </Box>

                    {/* ALL 3 PAYMENT METHODS: Cash, UPI, Split */}
                    <Box sx={{ display: 'flex', gap: 0.8, mt: 1 }}>
                        <Button 
                            variant="contained" 
                            fullWidth 
                            disabled={activeCart.length === 0}
                            onClick={() => {
                                setMobileCartOpen(false);
                                handleQuickSettle('cash');
                            }}
                            startIcon={<PaymentsIcon sx={{ fontSize: '18px !important' }} />}
                            sx={{ 
                                flex: 1.1,
                                py: 1.2, 
                                fontWeight: 900, 
                                fontSize: '0.88rem',
                                bgcolor: '#008751', 
                                borderRadius: 2.5, 
                                textTransform: 'none',
                                boxShadow: '0 2px 8px rgba(0,135,81,0.3)',
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#006f42' }
                            }}
                        >
                            💵 रोख
                        </Button>

                        <Button 
                            variant="contained" 
                            fullWidth 
                            disabled={activeCart.length === 0}
                            onClick={() => {
                                setMobileCartOpen(false);
                                handleQuickSettle('upi');
                            }}
                            startIcon={<QrCodeIcon sx={{ fontSize: '18px !important' }} />}
                            sx={{ 
                                flex: 1,
                                py: 1.2, 
                                fontWeight: 900, 
                                fontSize: '0.88rem',
                                bgcolor: '#0284c7', 
                                borderRadius: 2.5, 
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#0369a1' }
                            }}
                        >
                            📱 UPI
                        </Button>

                        <Button 
                            variant="outlined" 
                            fullWidth 
                            disabled={activeCart.length === 0}
                            onClick={() => {
                                setMobileCartOpen(false);
                                handleQuickSettle('split');
                            }}
                            startIcon={<AccountBalanceWalletIcon sx={{ fontSize: '16px !important' }} />}
                            sx={{ 
                                flex: 0.9,
                                py: 1.2, 
                                fontWeight: 900, 
                                fontSize: '0.82rem',
                                color: '#334155',
                                borderColor: '#cbd5e1',
                                bgcolor: '#ffffff',
                                borderRadius: 2.5, 
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                            }}
                        >
                            ⚡ स्प्लिट
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            {/* 5. UPI DYNAMIC QR CODE MODAL */}
            <Dialog 
                open={openUpiQr} 
                onClose={() => setOpenUpiQr(false)}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 360, p: 2, textAlign: 'center' } }}
            >
                <DialogTitle sx={{ fontWeight: 900, pb: 0.5, fontSize: '1.2rem' }}>
                    {l.upiTitle}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>
                        {l.upiHelp}
                    </Typography>

                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#008751', my: 1 }}>
                        ₹{finalTotal.toFixed(2)}
                    </Typography>

                    {/* QR Code Frame */}
                    <Box sx={{ 
                        p: 1.5, 
                        border: '2px solid #008751', 
                        borderRadius: 3, 
                        bgcolor: '#ffffff', 
                        display: 'inline-block',
                        boxShadow: '0 4px 16px rgba(0,135,81,0.15)',
                        mb: 1.5
                    }}>
                        <img 
                            src={qrCodeApiUrl} 
                            alt="UPI Payment QR Code" 
                            style={{ width: 180, height: 180, display: 'block', borderRadius: 8 }}
                        />
                    </Box>

                    <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, display: 'block' }}>
                        UPI ID: <span style={{ color: '#0284c7' }}>{upiVpa}</span>
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
                    <Button 
                        variant="contained" 
                        fullWidth 
                        onClick={() => executeSettlement('upi')}
                        startIcon={<CheckCircleIcon />}
                        sx={{ 
                            py: 1.2, 
                            fontWeight: 900, 
                            borderRadius: 2.5, 
                            bgcolor: '#008751', 
                            color: '#ffffff',
                            fontSize: '0.95rem',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#006f42' }
                        }}
                    >
                        {l.confirmPaid}
                    </Button>
                    <Button 
                        variant="text" 
                        fullWidth 
                        onClick={() => setOpenUpiQr(false)}
                        sx={{ fontWeight: 700, color: '#64748b', textTransform: 'none' }}
                    >
                        रद्द करा (Cancel)
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 6. SPLIT PAYMENT DIALOG */}
            <Dialog 
                open={openSplitDialog} 
                onClose={() => setOpenSplitDialog(false)}
                PaperProps={{ sx: { borderRadius: 3, width: '100%', maxWidth: 400, p: 1.5 } }}
            >
                <DialogTitle sx={{ fontWeight: 900 }}>
                    स्प्लिट पेमेंट (Cash + UPI)
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                        एकूण देय रक्कम: <strong style={{ color: '#008751', fontSize: '1.1rem' }}>₹{finalTotal.toFixed(2)}</strong>
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField 
                                label="रोख रक्कम (Cash ₹)"
                                type="number"
                                fullWidth
                                size="small"
                                value={splitCashAmount}
                                onChange={(e) => {
                                    const val = Math.max(0, parseFloat(e.target.value) || 0);
                                    setSplitCashAmount(val);
                                    setSplitUpiAmount(Math.max(0, finalTotal - val));
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField 
                                label="UPI रक्कम (UPI ₹)"
                                type="number"
                                fullWidth
                                size="small"
                                value={splitUpiAmount}
                                onChange={(e) => {
                                    const val = Math.max(0, parseFloat(e.target.value) || 0);
                                    setSplitUpiAmount(val);
                                    setSplitCashAmount(Math.max(0, finalTotal - val));
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenSplitDialog(false)} color="inherit">रद्द करा</Button>
                    <Button 
                        variant="contained" 
                        onClick={() => executeSettlement('split')}
                        sx={{ bgcolor: '#008751', fontWeight: 900, '&:hover': { bgcolor: '#006f42' } }}
                    >
                        कन्फर्म व टोकन जनरेट करा
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 7. BILL & TOKEN SUCCESS PRINT MODAL */}
            <Dialog 
                open={openReceipt} 
                onClose={() => setOpenReceipt(false)}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 420, bgcolor: '#f8fafc', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                            {l.billSettled}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setOpenReceipt(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 2 }}>
                    
                    {/* HUGE TOKEN NUMBER CARD */}
                    <Box sx={{ 
                        bgcolor: '#008751', 
                        color: '#ffffff', 
                        borderRadius: 3, 
                        p: 2.5, 
                        textAlign: 'center',
                        boxShadow: '0 6px 20px rgba(0,135,81,0.35)',
                        mb: 2
                    }}>
                        <Typography variant="caption" sx={{ letterSpacing: 2, fontWeight: 800, opacity: 0.9, textTransform: 'uppercase' }}>
                            {l.tokenNumber}
                        </Typography>
                        <Typography variant="h2" sx={{ fontWeight: 900, my: 0.5 }}>
                            {`#${settledOrderDetails?.coupon_number || settledOrderDetails?.token_number || (settledOrderDetails?.id ? settledOrderDetails.id.replace('ORD-', '') : '1')}`}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, opacity: 0.95 }}>
                            रक्कम: ₹{Number(settledOrderDetails?.total || 0).toFixed(0)} ({settledOrderDetails?.paymentMethod || 'Cash'})
                        </Typography>
                    </Box>

                    {/* Change Calculator (रोख रकमेची परतफेड) */}
                    <Box sx={{ bgcolor: '#ffffff', p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'block', mb: 0.8 }}>
                            💵 {l.cashReceived} & {l.changeToReturn}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField 
                                size="small"
                                placeholder="उदा. 100, 200, 500"
                                type="number"
                                value={receivedCash}
                                onChange={(e) => setReceivedCash(e.target.value)}
                                sx={{ flex: 1 }}
                                InputProps={{
                                    startAdornment: <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#64748b', mr: 0.5 }}>₹</Typography>,
                                    sx: { borderRadius: 2 }
                                }}
                            />
                            {parsedReceived > 0 && (
                                <Box sx={{ bgcolor: '#f0fdf4', px: 1.5, py: 0.8, borderRadius: 2, border: '1px solid #bbf7d0' }}>
                                    <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, display: 'block', lineHeight: 1 }}>
                                        परत द्या:
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ color: '#16a34a', fontWeight: 900, lineHeight: 1.1 }}>
                                        ₹{changeToReturn.toFixed(0)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Receipt Image Canvas Preview */}
                    {receiptPreviewImage && (
                        <Box 
                            id="counter-receipt-paper" 
                            sx={{ 
                                bgcolor: '#ffffff', 
                                p: 0, 
                                borderRadius: 2, 
                                border: '1px dashed #cbd5e1', 
                                display: 'flex', 
                                justifyContent: 'center',
                                maxHeight: 220,
                                overflowY: 'auto'
                            }}
                        >
                            <img src={receiptPreviewImage} alt="Receipt Preview" style={{ width: '100%', maxWidth: '100%', height: 'auto', display: 'block', margin: 0, padding: 0 }} />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 0, flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button 
                            variant="contained" 
                            fullWidth 
                            startIcon={<LocalPrintshopIcon />}
                            onClick={() => { printOrderReceipt(settledOrderDetails, localStorage.getItem('dinexa_printer_lang') || currentLanguage); }}
                            sx={{ py: 1, fontWeight: 800, borderRadius: 2, bgcolor: '#1e293b', textTransform: 'none', '&:hover': { bgcolor: '#0f172a' } }}
                        >
                            सिस्टम प्रिंट (Print)
                        </Button>
                        <Button 
                            variant="contained" 
                            fullWidth 
                            startIcon={<LocalPrintshopIcon />}
                            onClick={handleBtPrint}
                            sx={{ py: 1, fontWeight: 800, borderRadius: 2, bgcolor: '#0284c7', textTransform: 'none', '&:hover': { bgcolor: '#0369a1' } }}
                        >
                            {btDeviceName ? `BT: ${btDeviceName}` : 'ब्लूटूथ प्रिंट'}
                        </Button>
                    </Box>

                    <Button 
                        variant="contained" 
                        fullWidth 
                        onClick={() => setOpenReceipt(false)}
                        sx={{ py: 1.2, fontWeight: 900, borderRadius: 2.5, bgcolor: '#008751', textTransform: 'none', '&:hover': { bgcolor: '#006f42' } }}
                    >
                        🚀 {l.nextOrder}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
